import React, { useEffect, useState, useCallback } from 'react'
import * as RadixToast from '@radix-ui/react-toast'
import { X, CheckCircle2, AlertCircle, AlertTriangle, Info } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { ToastMessage } from '@/types'

// ─── Global Toast State ───────────────────────────────────────────────────────
let toastQueue: Array<(msg: Omit<ToastMessage, 'id'>) => void> = []

export function toast(msg: Omit<ToastMessage, 'id'>) {
  toastQueue.forEach((fn) => fn(msg))
}

toast.success = (title: string, description?: string) =>
  toast({ type: 'success', title, description })

toast.error = (title: string, description?: string) =>
  toast({ type: 'error', title, description })

toast.warning = (title: string, description?: string) =>
  toast({ type: 'warning', title, description })

toast.info = (title: string, description?: string) =>
  toast({ type: 'info', title, description })

// ─── Icons & Styles per type ──────────────────────────────────────────────────
const typeConfig = {
  success: {
    icon:  CheckCircle2,
    ring:  'ring-emerald-200 dark:ring-emerald-800',
    icon2: 'text-emerald-500',
    bg:    'bg-white dark:bg-slate-800',
  },
  error: {
    icon:  AlertCircle,
    ring:  'ring-red-200 dark:ring-red-800',
    icon2: 'text-sport-red',
    bg:    'bg-white dark:bg-slate-800',
  },
  warning: {
    icon:  AlertTriangle,
    ring:  'ring-amber-200 dark:ring-amber-800',
    icon2: 'text-sport-orange',
    bg:    'bg-white dark:bg-slate-800',
  },
  info: {
    icon:  Info,
    ring:  'ring-sky-200 dark:ring-sky-800',
    icon2: 'text-primary-500',
    bg:    'bg-white dark:bg-slate-800',
  },
}

// ─── Toast Provider ───────────────────────────────────────────────────────────
export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [messages, setMessages] = useState<ToastMessage[]>([])

  const addToast = useCallback((msg: Omit<ToastMessage, 'id'>) => {
    const id = crypto.randomUUID()
    setMessages((prev) => [...prev, { ...msg, id }])
  }, [])

  // Register in global queue
  useEffect(() => {
    toastQueue.push(addToast)
    return () => {
      toastQueue = toastQueue.filter((fn) => fn !== addToast)
    }
  }, [addToast])

  // Also listen to sporokulu:toast DOM events (from api.ts)
  useEffect(() => {
    const handler = (e: Event) => {
      const { message, type } = (e as CustomEvent).detail
      addToast({ type, title: message })
    }
    window.addEventListener('sporokulu:toast', handler)
    return () => window.removeEventListener('sporokulu:toast', handler)
  }, [addToast])

  const remove = (id: string) =>
    setMessages((prev) => prev.filter((m) => m.id !== id))

  return (
    <RadixToast.Provider swipeDirection="right" duration={4500}>
      {children}

      {messages.map((msg) => {
        const cfg = typeConfig[msg.type]
        const Icon = cfg.icon

        return (
          <RadixToast.Root
            key={msg.id}
            open
            onOpenChange={(open) => !open && remove(msg.id)}
            className={cn(
              'flex items-start gap-3 p-4 pr-8 rounded-xl shadow-card-lg',
              'ring-1 ring-inset',
              cfg.bg,
              cfg.ring,
              'data-[state=open]:animate-slide-up',
              'data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)]',
              'data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)]',
              'data-[swipe=cancel]:translate-x-0',
              'data-[swipe=cancel]:transition-transform',
              'relative min-w-[280px] max-w-sm',
            )}
            aria-live="polite"
          >
            <Icon className={cn('h-5 w-5 mt-0.5 flex-shrink-0', cfg.icon2)} aria-hidden="true" />

            <div className="flex-1 min-w-0">
              <RadixToast.Title className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                {msg.title}
              </RadixToast.Title>
              {msg.description && (
                <RadixToast.Description className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                  {msg.description}
                </RadixToast.Description>
              )}
            </div>

            <RadixToast.Close
              className={cn(
                'absolute top-2.5 right-2.5 rounded-lg p-0.5',
                'text-slate-400 hover:text-slate-600 dark:hover:text-slate-200',
                'hover:bg-slate-100 dark:hover:bg-slate-700',
                'transition-colors duration-150',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
              )}
              aria-label="Kapat"
            >
              <X className="h-3.5 w-3.5" />
            </RadixToast.Close>
          </RadixToast.Root>
        )
      })}

      <RadixToast.Viewport
        className={cn(
          'fixed bottom-4 right-4 z-toast',
          'flex flex-col gap-2',
          'max-h-screen w-auto',
          'outline-none',
        )}
        label="Bildirimler"
      />
    </RadixToast.Provider>
  )
}
